import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { API, toast } from '@/App';
import axios from 'axios';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const AdminSettings = () => {
  const [formData, setFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    // Kurumsal Künye
    legal_name: '',
    tax_office: '',
    tax_number: '',
    mersis_no: '',
    trade_registry_no: '',
    social_links: {
      linkedin: '',
      twitter: '',
      instagram: '',
      github: '',
    },
    // Hero Section
    hero_title: '',
    hero_title_highlight: '',
    hero_description: '',
    hero_cta_primary_text: '',
    hero_cta_primary_link: '',
    hero_cta_secondary_text: '',
    hero_cta_secondary_link: '',
    // Stats
    stats: [],
    // Footer
    footer_description: '',
    footer_copyright: '',
    // SEO
    site_title: '',
    meta_description: '',
    meta_keywords: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/admin`);
      setFormData({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_user: response.data.smtp_user || '',
        smtp_password: response.data.smtp_password || '',
        smtp_from_email: response.data.smtp_from_email || '',
        company_email: response.data.company_email || '',
        company_phone: response.data.company_phone || '',
        company_address: response.data.company_address || '',
        // Kurumsal Künye
        legal_name: response.data.legal_name || '',
        tax_office: response.data.tax_office || '',
        tax_number: response.data.tax_number || '',
        mersis_no: response.data.mersis_no || '',
        trade_registry_no: response.data.trade_registry_no || '',
        social_links: response.data.social_links || {
          linkedin: '',
          twitter: '',
          instagram: '',
          github: '',
        },
        // Hero
        hero_title: response.data.hero_title || '',
        hero_title_highlight: response.data.hero_title_highlight || '',
        hero_description: response.data.hero_description || '',
        hero_cta_primary_text: response.data.hero_cta_primary_text || '',
        hero_cta_primary_link: response.data.hero_cta_primary_link || '',
        hero_cta_secondary_text: response.data.hero_cta_secondary_text || '',
        hero_cta_secondary_link: response.data.hero_cta_secondary_link || '',
        // Stats
        stats: response.data.stats || [],
        // Footer
        footer_description: response.data.footer_description || '',
        footer_copyright: response.data.footer_copyright || '',
        // SEO
        site_title: response.data.site_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || '',
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, formData);
      toast.success('Ayarlar kaydedildi!');
    } catch (error) {
      toast.error('Bir hata oluştu!');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Ayarlar</h1>
          <p className="text-white/60">Site ayarlarını yönetin</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SMTP Settings */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">SMTP Ayarları</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Host</label>
                <Input
                  value={formData.smtp_host}
                  onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Port</label>
                <Input
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Kullanıcı Adı</label>
                <Input
                  value={formData.smtp_user}
                  onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                  placeholder="user@example.com"
                  data-testid="smtp-user-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SMTP Şifre</label>
                <Input
                  type="password"
                  value={formData.smtp_password}
                  onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="smtp-password-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Gönderen E-posta</label>
                <Input
                  value={formData.smtp_from_email}
                  onChange={(e) => setFormData({ ...formData, smtp_from_email: e.target.value })}
                  placeholder="noreply@eneryazilim.com"
                  data-testid="smtp-from-email-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">Firma Bilgileri</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Firma E-posta</label>
                <Input
                  value={formData.company_email}
                  onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                  placeholder="info@eneryazilim.com"
                  data-testid="company-email-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Firma Telefon</label>
                <Input
                  value={formData.company_phone}
                  onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                  placeholder="+90 XXX XXX XX XX"
                  data-testid="company-phone-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Firma Adresi</label>
                <Textarea
                  value={formData.company_address}
                  onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                  placeholder="Türkiye"
                  rows={3}
                  data-testid="company-address-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Kurumsal Künye */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">Kurumsal Künye</h2>
            <p className="text-sm text-white/60 mb-4">
              Bu bilgiler iletişim sayfasında ve footer'da yayınlanır. Vergi levhası ve ticaret sicil gazetesi ile birebir aynı olmalıdır.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Ticari Unvan (tam yasal unvan)</label>
                <Input
                  value={formData.legal_name}
                  onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                  placeholder="Ener Yazılım ... Limited Şirketi"
                  data-testid="legal-name-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Vergi Dairesi</label>
                <Input
                  value={formData.tax_office}
                  onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })}
                  placeholder="Gaziemir"
                  data-testid="tax-office-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Vergi Kimlik No (VKN)</label>
                <Input
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  placeholder="10 haneli VKN"
                  data-testid="tax-number-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">MERSİS No</label>
                <Input
                  value={formData.mersis_no}
                  onChange={(e) => setFormData({ ...formData, mersis_no: e.target.value })}
                  placeholder="16 haneli MERSİS numarası"
                  data-testid="mersis-no-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Ticaret Sicil No</label>
                <Input
                  value={formData.trade_registry_no}
                  onChange={(e) => setFormData({ ...formData, trade_registry_no: e.target.value })}
                  placeholder="İzmir Ticaret Sicil No"
                  data-testid="trade-registry-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">Sosyal Medya Bağlantıları</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">LinkedIn</label>
                <Input
                  value={formData.social_links.linkedin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, linkedin: e.target.value },
                    })
                  }
                  placeholder="https://linkedin.com/company/..."
                  data-testid="linkedin-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Twitter</label>
                <Input
                  value={formData.social_links.twitter}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, twitter: e.target.value },
                    })
                  }
                  placeholder="https://twitter.com/..."
                  data-testid="twitter-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Instagram</label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, instagram: e.target.value },
                    })
                  }
                  placeholder="https://instagram.com/..."
                  data-testid="instagram-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">GitHub</label>
                <Input
                  value={formData.social_links.github}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, github: e.target.value },
                    })
                  }
                  placeholder="https://github.com/..."
                  data-testid="github-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Hero Section Settings */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">Ana Sayfa Hero Bölümü</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ana Başlık</label>
                <Input
                  value={formData.hero_title}
                  onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                  placeholder="Dijital Dünyanın"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Vurgulu Başlık</label>
                <Input
                  value={formData.hero_title_highlight}
                  onChange={(e) => setFormData({ ...formData, hero_title_highlight: e.target.value })}
                  placeholder="Güvenli Geleceği"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Açıklama</label>
                <Textarea
                  value={formData.hero_description}
                  onChange={(e) => setFormData({ ...formData, hero_description: e.target.value })}
                  placeholder="Açıklama metni..."
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Birinci Buton Metni</label>
                  <Input
                    value={formData.hero_cta_primary_text}
                    onChange={(e) => setFormData({ ...formData, hero_cta_primary_text: e.target.value })}
                    placeholder="Hizmetlerimiz"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Birinci Buton Linki</label>
                  <Input
                    value={formData.hero_cta_primary_link}
                    onChange={(e) => setFormData({ ...formData, hero_cta_primary_link: e.target.value })}
                    placeholder="/hizmetler"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">İkinci Buton Metni</label>
                  <Input
                    value={formData.hero_cta_secondary_text}
                    onChange={(e) => setFormData({ ...formData, hero_cta_secondary_text: e.target.value })}
                    placeholder="İletişime Geç"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">İkinci Buton Linki</label>
                  <Input
                    value={formData.hero_cta_secondary_link}
                    onChange={(e) => setFormData({ ...formData, hero_cta_secondary_link: e.target.value })}
                    placeholder="/iletisim"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">İstatistikler</h2>
            <p className="text-sm text-white/60 mb-4">
              İstatistikler JSON formatında. İkon değerleri: Code, Users, Shield, Zap
            </p>
            <Textarea
              value={JSON.stringify(formData.stats, null, 2)}
              onChange={(e) => {
                try {
                  setFormData({ ...formData, stats: JSON.parse(e.target.value) });
                } catch (err) {
                  // Invalid JSON, keep typing
                }
              }}
              placeholder='[{"icon": "Code", "value": "500+", "label": "Proje"}]'
              className="bg-white/5 border-white/10 text-white font-mono"
              rows={8}
            />
          </div>

          {/* Footer Settings */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">Footer Ayarları</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Footer Açıklama</label>
                <Textarea
                  value={formData.footer_description}
                  onChange={(e) => setFormData({ ...formData, footer_description: e.target.value })}
                  placeholder="Şirket açıklaması..."
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Copyright Metni</label>
                <Input
                  value={formData.footer_copyright}
                  onChange={(e) => setFormData({ ...formData, footer_copyright: e.target.value })}
                  placeholder="© 2026 Ener Yazılım"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="glass p-6 rounded-2xl">
            <h2 className="text-xl font-semibold mb-4">SEO Ayarları</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Site Başlığı</label>
                <Input
                  value={formData.site_title}
                  onChange={(e) => setFormData({ ...formData, site_title: e.target.value })}
                  placeholder="Ener Yazılım - Dijital Dünyanın Güvenli Geleceği"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Meta Açıklama</label>
                <Textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Site açıklaması..."
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Meta Keywords (virgülle ayırın)</label>
                <Input
                  value={formData.meta_keywords}
                  onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                  placeholder="yazılım, siber güvenlik, AI"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="save-settings-button"
            className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E]"
          >
            {loading ? 'Kaydediliyor...' : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Ayarları Kaydet
              </>
            )}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;